/**
 * Customized Button component.
 *
 * Reference: https://chakra-ui.com/docs/theming/customize-theme
 * Figma Reference: https://www.figma.com/file/Z8kfGR6BNKMm3XvQGhKcBD/PINS-Advantage-2021-Design-System?node-id=5%3A3
 */

import colors from '../foundation/colors';
import {
  generatePrimaryHoverBubbleStyle,
  generateSecondaryHoverBubbleStyle,
  generateTextStyling
} from '../util/generateButtonStyles';

const Button = {
  baseStyle: {
    textStyle: 'button',
    borderRadius: 'md',
    _hover: {
      cursor: 'pointer',
      _disabled: { cursor: 'not-allowed', opacity: 0.4 }
    },
    _focus: {
      outline: 'none',
      boxShadow: 'none'
    },
    position: 'relative',
    fontWeight: '500'
  },
  variants: {
    primary: props => {
      const rounded = props?.isRound ? '4xl' : 'md';
      const primaryBackground = colors.primaryScale['600'];
      const activeCircleBackground = colors.primaryScale['700'];
      const hoverStyle = generatePrimaryHoverBubbleStyle(primaryBackground, activeCircleBackground);

      return {
        ...hoverStyle,
        borderRadius: rounded,
        color: 'white',
        rightIcon: {},
        leftIcon: {}
      };
    },
    primarySuccess: props => {
      const rounded = props?.isRound ? '4xl' : 'md';
      const primaryBackground = colors.success.primary['600'];
      const activeCircleBackground = colors.success.primary['900'];
      const hoverStyle = generatePrimaryHoverBubbleStyle(primaryBackground, activeCircleBackground);

      return {
        ...hoverStyle,
        borderRadius: rounded,
        color: 'white',
        rightIcon: {},
        leftIcon: {}
      };
    },
    primaryCaution: props => {
      const rounded = props?.isRound ? '4xl' : 'md';
      const primaryBackground = colors.caution.primary['600'];
      const activeCircleBackground = colors.caution.primary['900'];
      const hoverStyle = generatePrimaryHoverBubbleStyle(primaryBackground, activeCircleBackground);

      return {
        ...hoverStyle,
        borderRadius: rounded,
        color: 'white',
        rightIcon: {},
        leftIcon: {}
      };
    },
    primaryError: props => {
      const rounded = props?.isRound ? '4xl' : 'md';
      const primaryBackground = colors.error.primary['600'];
      const activeCircleBackground = colors.error.primary['900'];
      const hoverStyle = generatePrimaryHoverBubbleStyle(primaryBackground, activeCircleBackground);

      return {
        ...hoverStyle,
        borderRadius: rounded,
        color: 'white',
        rightIcon: {},
        leftIcon: {}
      };
    },
    accent: props => {
      const rounded = props?.isRound ? '4xl' : 'md';
      const primaryBackground = colors.primaryScale['50'];
      const activeCircleBackground = colors.primaryScale['100'];
      const fontHoverColor = colors.primaryScale['700'];
      const borderHoverColor = colors.primaryScale['700'];
      const disabledFontColor = colors.primaryScale['600'];
      const disabledBorderColor = colors.primaryScale['600'];
      const activeFontColor = colors.primaryScale['700'];
      const hoverStyle = generateSecondaryHoverBubbleStyle(
        primaryBackground,
        activeCircleBackground,
        fontHoverColor,
        borderHoverColor,
        disabledFontColor,
        disabledBorderColor,
        activeFontColor
      );

      return {
        ...hoverStyle,
        borderRadius: rounded,
        color: colors.primaryScale['600'],
        rightIcon: {},
        leftIcon: {}
      };
    },
    accentSuccess: props => {
      const rounded = props?.isRound ? '4xl' : 'md';
      const primaryBackground = colors.success.primary['50'];
      const activeCircleBackground = '#B9E6CA';
      const fontHoverColor = colors.success.primary['900'];
      const borderHoverColor = colors.success.primary['900'];
      const disabledFontColor = colors.success.primary['600'];
      const disabledBorderColor = colors.success.primary['600'];
      const activeFontColor = 'white';
      const hoverStyle = generateSecondaryHoverBubbleStyle(
        primaryBackground,
        activeCircleBackground,
        fontHoverColor,
        borderHoverColor,
        disabledFontColor,
        disabledBorderColor,
        activeFontColor
      );

      return {
        ...hoverStyle,
        borderRadius: rounded,
        color: colors.success.primary['600'],
        rightIcon: {},
        leftIcon: {}
      };
    },
    accentCaution: props => {
      const rounded = props?.isRound ? '4xl' : 'md';
      const primaryBackground = colors.caution.primary['50'];
      const activeCircleBackground = colors.caution.primary['600'];
      const fontHoverColor = colors.caution.primary['900'];
      const borderHoverColor = colors.caution.primary['900'];
      const disabledFontColor = colors.caution.primary['600'];
      const disabledBorderColor = colors.caution.primary['600'];
      const activeFontColor = 'white';
      const hoverStyle = generateSecondaryHoverBubbleStyle(
        primaryBackground,
        activeCircleBackground,
        fontHoverColor,
        borderHoverColor,
        disabledFontColor,
        disabledBorderColor,
        activeFontColor
      );

      return {
        ...hoverStyle,
        borderRadius: rounded,
        color: colors.caution.primary['600'],
        rightIcon: {},
        leftIcon: {}
      };
    },
    accentError: props => {
      const rounded = props?.isRound ? '4xl' : 'md';
      const primaryBackground = colors.error.primary['50'];
      const activeCircleBackground = colors.error.primary['600'];
      const fontHoverColor = colors.error.primary['900'];
      const borderHoverColor = colors.error.primary['900'];
      const disabledFontColor = colors.error.primary['600'];
      const disabledBorderColor = colors.error.primary['600'];
      const activeFontColor = 'white';
      const hoverStyle = generateSecondaryHoverBubbleStyle(
        primaryBackground,
        activeCircleBackground,
        fontHoverColor,
        borderHoverColor,
        disabledFontColor,
        disabledBorderColor,
        activeFontColor
      );

      return {
        ...hoverStyle,
        borderRadius: rounded,
        color: colors.error.primary['600'],
        rightIcon: {},
        leftIcon: {}
      };
    },
    accentBlack: props => {
      const rounded = props?.isRound ? '4xl' : 'md';
      const primaryBackground = colors.greyScale['100'];
      const activeCircleBackground = colors.greyScale['800'];
      const fontHoverColor = colors.greyScale['900'];
      const borderHoverColor = colors.greyScale['900'];
      const disabledFontColor = colors.greyScale['600'];
      const disabledBorderColor = colors.greyScale['600'];
      const activeFontColor = 'white';
      const hoverStyle = generateSecondaryHoverBubbleStyle(
        primaryBackground,
        activeCircleBackground,
        fontHoverColor,
        borderHoverColor,
        disabledFontColor,
        disabledBorderColor,
        activeFontColor
      );

      return {
        ...hoverStyle,
        borderRadius: rounded,
        color: colors.greyScale['900'],
        rightIcon: {},
        leftIcon: {}
      };
    },
    secondary: props => {
      const rounded = props?.isRound ? '4xl' : 'md';
      const primaryBackground = 'transparent';
      const activeCircleBackground = colors.primaryScale['50'];
      const fontHoverColor = colors.primaryScale['700'];
      const borderHoverColor = colors.primaryScale['700'];
      const disabledFontColor = colors.primaryScale['600'];
      const disabledBorderColor = colors.primaryScale['600'];
      const activeFontColor = colors.primaryScale['700'];
      const hoverStyle = generateSecondaryHoverBubbleStyle(
        primaryBackground,
        activeCircleBackground,
        fontHoverColor,
        borderHoverColor,
        disabledFontColor,
        disabledBorderColor,
        activeFontColor
      );

      return {
        ...hoverStyle,
        color: colors.primaryScale['600'],
        borderColor: colors.primaryScale['600'],
        borderRadius: rounded,
        borderWidth: '1px'
      };
    },
    secondarySuccess: props => {
      const rounded = props?.isRound ? '4xl' : 'md';
      const primaryBackground = 'transparent';
      const activeCircleBackground = colors.success.primary['600'];
      const fontHoverColor = colors.success.primary['900'];
      const borderHoverColor = colors.success.primary['900'];
      const disabledFontColor = colors.success.primary['600'];
      const disabledBorderColor = colors.success.primary['600'];
      const activeFontColor = 'white';
      const hoverStyle = generateSecondaryHoverBubbleStyle(
        primaryBackground,
        activeCircleBackground,
        fontHoverColor,
        borderHoverColor,
        disabledFontColor,
        disabledBorderColor,
        activeFontColor
      );

      return {
        ...hoverStyle,
        color: colors.success.primary['600'],
        borderColor: colors.success.primary['600'],
        borderRadius: rounded,
        borderWidth: '1px'
      };
    },
    secondaryCaution: props => {
      const rounded = props?.isRound ? '4xl' : 'md';
      const primaryBackground = 'transparent';
      const activeCircleBackground = colors.caution.primary['600'];
      const fontHoverColor = colors.caution.primary['900'];
      const borderHoverColor = colors.caution.primary['900'];
      const disabledFontColor = colors.caution.primary['600'];
      const disabledBorderColor = colors.caution.primary['600'];
      const activeFontColor = 'white';
      const hoverStyle = generateSecondaryHoverBubbleStyle(
        primaryBackground,
        activeCircleBackground,
        fontHoverColor,
        borderHoverColor,
        disabledFontColor,
        disabledBorderColor,
        activeFontColor
      );

      return {
        ...hoverStyle,
        color: colors.caution.primary['600'],
        borderColor: colors.caution.primary['600'],
        borderRadius: rounded,
        borderWidth: '1px'
      };
    },
    secondaryError: props => {
      const rounded = props?.isRound ? '4xl' : 'md';
      const primaryBackground = 'transparent';
      const activeCircleBackground = colors.error.primary['600'];
      const fontHoverColor = colors.error.primary['900'];
      const borderHoverColor = colors.error.primary['900'];
      const disabledFontColor = colors.error.primary['600'];
      const disabledBorderColor = colors.error.primary['600'];
      const activeFontColor = 'white';
      const hoverStyle = generateSecondaryHoverBubbleStyle(
        primaryBackground,
        activeCircleBackground,
        fontHoverColor,
        borderHoverColor,
        disabledFontColor,
        disabledBorderColor,
        activeFontColor
      );

      return {
        ...hoverStyle,
        color: colors.error.primary['600'],
        borderColor: colors.error.primary['600'],
        borderRadius: rounded,
        borderWidth: '1px'
      };
    },
    secondaryBlack: props => {
      const rounded = props?.isRound ? '4xl' : 'md';
      const primaryBackground = 'transparent';
      const activeCircleBackground = colors.greyScale['600'];
      const fontHoverColor = colors.greyScale['900'];
      const borderHoverColor = colors.greyScale['900'];
      const disabledFontColor = colors.greyScale['600'];
      const disabledBorderColor = colors.greyScale['600'];
      const activeFontColor = 'white';
      const hoverStyle = generateSecondaryHoverBubbleStyle(
        primaryBackground,
        activeCircleBackground,
        fontHoverColor,
        borderHoverColor,
        disabledFontColor,
        disabledBorderColor,
        activeFontColor
      );

      return {
        ...hoverStyle,
        color: colors.greyScale['900'],
        borderColor: colors.greyScale['900'],
        borderRadius: rounded,
        borderWidth: '1px'
      };
    },
    textPrimary: () => {
      const mainColor = colors.primaryScale['600'];
      const hoverColor = colors.primaryScale['700'];
      const activeFontColor = colors.primaryScale['600'];
      const activeBgColor = colors.primaryScale['50'];
      const textStyling = generateTextStyling(mainColor, hoverColor, activeFontColor, activeBgColor);

      return { ...textStyling };
    },
    textSuccess: () => {
      const mainColor = colors.success.primary['600'];
      const hoverColor = colors.success.primary['900'];
      const activeFontColor = colors.success.primary['900'];
      const activeBgColor = colors.success.primary['50'];
      const textStyling = generateTextStyling(mainColor, hoverColor, activeFontColor, activeBgColor);

      return { ...textStyling };
    },
    textCaution: () => {
      const mainColor = colors.caution.primary['600'];
      const hoverColor = colors.caution.primary['900'];
      const activeFontColor = colors.caution.primary['900'];
      const activeBgColor = colors.caution.primary['50'];
      const textStyling = generateTextStyling(mainColor, hoverColor, activeFontColor, activeBgColor);

      return { ...textStyling };
    },
    textError: () => {
      const mainColor = colors.error.primary['600'];
      const hoverColor = colors.error.primary['900'];
      const activeFontColor = colors.error.primary['900'];
      const activeBgColor = colors.error.primary['50'];
      const textStyling = generateTextStyling(mainColor, hoverColor, activeFontColor, activeBgColor);

      return { ...textStyling };
    },
    textBlack: () => {
      const mainColor = colors.greyScale['900'];
      const hoverColor = colors.greyScale['700'];
      const activeFontColor = colors.greyScale['700'];
      const activeBgColor = colors.greyScale['200'];
      const textStyling = generateTextStyling(mainColor, hoverColor, activeFontColor, activeBgColor);

      return { ...textStyling };
    },
    textGrey: () => {
      const mainColor = colors.greyScale['600'];
      const hoverColor = colors.greyScale['700'];
      const activeFontColor = colors.greyScale['700'];
      const activeBgColor = colors.greyScale['200'];
      const textStyling = generateTextStyling(mainColor, hoverColor, activeFontColor, activeBgColor);

      return { ...textStyling };
    },
    textWhite: () => {
      const mainColor = colors.greyScale['50'];
      const hoverColor = colors.greyScale['400'];
      const activeFontColor = colors.greyScale['400'];
      const activeBgColor = 'transparent';
      const textStyling = generateTextStyling(mainColor, hoverColor, activeFontColor, activeBgColor);

      return { ...textStyling };
    },
    gradientPrimary: props => {
      const isRounded = props?.isRound ? '4xl' : 'md';

      return {
        _disabled: { opacity: 0.5 },
        color: 'white',
        borderRadius: isRounded,
        backgroundImage: colors.gradient.buttonPrimary,
        transition: '0.5s',
        backgroundSize: '200% auto',
        _hover: {
          backgroundPosition: 'right',
          _disabled: {
            backgroundImage: colors.gradient.buttonPrimary,
            cursor: 'not-allowed'
          }
        }
      };
    },
    gradientAi: props => {
      const isRounded = props?.isRound ? '4xl' : 'md';

      return {
        _disabled: { opacity: 0.5 },
        color: 'white',
        borderRadius: isRounded,
        backgroundImage: colors.gradient.aiPrimary,
        transition: '0.5s',
        backgroundSize: '150% auto',
        _hover: {
          backgroundPosition: 'right',
          _disabled: {
            backgroundImage: colors.gradient.buttonPrimary,
            cursor: 'not-allowed'
          }
        }
      };
    },
    gradientSuccess: props => {
      const isRounded = props?.isRound ? '4xl' : 'md';

      return {
        color: 'white',
        borderRadius: isRounded,
        backgroundImage: colors.gradient.buttonSuccess,
        transition: '0.5s',
        backgroundSize: '200% auto',
        _hover: {
          backgroundPosition: 'right center',
          _disabled: { background: colors.gradient.buttonSuccess }
        },
        _disabled: { opacity: 0.5 }
      };
    },
    gradientError: {
      color: 'white',
      borderRadius: '4xl',
      backgroundImage: colors.gradient.buttonError,
      transition: '0.5s',
      backgroundSize: '200% auto',
      _hover: {
        backgroundPosition: 'right center',
        _disabled: {
          backgroundImage: colors.gradient.buttonError,
          cursor: 'not-allowed'
        }
      }
    },
    gradientOutline: {
      color: colors.primaryScale['600'],
      borderColor: colors.gradient.primary,
      border: '1px solid',
      background: 'white',
      _hover: {
        background: colors.gradient.primary,
        color: 'white'
      }
    },
    aiGradientOutline: {
      border: '1px solid transparent',
      borderRadius: 'md',
      backgroundImage:
        `linear-gradient(white, white), ${colors.gradient.aiPrimary}`,
      backgroundOrigin: 'border-box',
      backgroundClip: 'padding-box, border-box',
      // color: colors.primaryScale['600'],
      transition: 'all .3s ease',
      _hover: {
        backgroundImage: colors.gradient.aiPrimary,
        color: 'white',
        _disabled: {
          cursor: 'not-allowed',
          opacity: 0.3,
          backgroundImage:
            `linear-gradient(white, white), ${colors.gradient.aiPrimary}`,
          backgroundOrigin: 'border-box',
          backgroundClip: 'padding-box, border-box',
          color: 'rgba(214, 48, 228, 0.1)'
        }
      }
    },
    aiGradientOutlineBlack: {
      border: '1px solid transparent',
      borderRadius: 'md',
      backgroundImage:
        `linear-gradient(black, black), ${colors.gradient.aiPrimary}`,
      backgroundOrigin: 'border-box',
      backgroundClip: 'padding-box, border-box',
      // color: colors.primaryScale['600'],
      transition: 'all .3s ease',
      _hover: {
        backgroundImage: colors.gradient.aiPrimary,
        color: 'white',
        _disabled: {
          cursor: 'not-allowed',
          opacity: 0.3,
          backgroundImage:
            `linear-gradient(white, white), ${colors.gradient.aiPrimary}`,
          backgroundOrigin: 'border-box',
          backgroundClip: 'padding-box, border-box',
          color: 'rgba(214, 48, 228, 0.1)'
        }
      }
    }
  },
  sizes: {
    xs: { h: '24px', fontSize: 'sm', fontWeight: '500' },
    sm: { h: '32px', fontSize: 'sm', fontWeight: '500' },
    md: { h: '40px', fontSize: 'sm', fontWeight: '500' },
    lg: { h: '46px', fontSize: 'sm', fontWeight: '500' },
    navIcon: { h: '48px', w: '48px' },
    navFull: {
      h: '56px',
      w: ['200px', '200px', '200px', '224px', '224px'],
      fontSize: '16px'
    }
  },
  defaultProps: {
    variant: 'primary',
    size: 'md',
    borderRadius: 'md',
    fontWeight: '500'
  }
};

export default Button;
